import type { List } from 'immutable';
import { Map } from 'immutable';
import React from 'react';
import { makeStyles } from 'tss-react/mui';

import CompareArrowsIcon from '@mui/icons-material/CompareArrows';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckIcon from '@mui/icons-material/DoneRounded';
import EditIcon from '@mui/icons-material/EditRounded';
import LinkIcon from '@mui/icons-material/Link';
import UnlinkIcon from '@mui/icons-material/LinkOff';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import LinkRoundedIcon from '@mui/icons-material/LinkRounded';
import OpenInBrowserIcon from '@mui/icons-material/OpenInBrowser';
import PaymentsOutlinedIcon from '@mui/icons-material/PaymentsOutlined';
import RepeatOneIcon from '@mui/icons-material/RepeatOneRounded';
import RepeatIcon from '@mui/icons-material/RepeatRounded';
import SubdirectoryArrowRightRoundedIcon from '@mui/icons-material/SubdirectoryArrowRightRounded';
import ListItemIcon from '@mui/material/ListItemIcon';

import { useTheme } from '@mui/material/styles';

import { accountsSelectors } from 'app-components/flux/accounts/index';
import { categoriesSelectors } from 'app-components/flux/categories';
import { chartOfAccountsSelectors, chartOfAccountsUtils } from 'app-components/flux/chart-of-accounts';
import { featureFlagsSelectors } from 'app-components/flux/feature-flags';
import type { Goal } from 'app-components/flux/goals';
import { goalsSelectors } from 'app-components/flux/goals';
import { rootUiActions, rootUiTypes } from 'app-components/flux/root-ui';
import type { Tag } from 'app-components/flux/tags/tagsTypes';
import { transactionsSelectors, transactionsTypes, transactionsUtils } from 'app-components/flux/transactions';
import { TransactionSubType } from 'app-components/flux/transactions/transactionsTypes';

import ExcludeFromReports from 'assets/icons/settings-menu/ExcludeFromReports.inline.svg';
import ExcludeFromSpendingPlan from 'assets/icons/settings-menu/ExcludeFromSpendingPlan.inline.svg';
import { HandleDisconnectOption } from 'components/BillPresentment/utils';
import { doBillPresentment } from 'components/Dialogs/BillPresentment/actions';
import { editReminderDialog } from 'components/Dialogs/EditReminderDialog/actions';
import { openInvestmentTransactionEditDialogAction } from 'components/Dialogs/InvestmentTransactionEditDialog';
import { showMatchTxnDialog } from 'components/Dialogs/MatchTxnDialog/actions';
import { DIALOG_TYPE_REFUND_EDIT } from 'components/Dialogs/RefundEditDialog';
import { showViewSeriesDialog } from 'components/Dialogs/ViewSeriesDialog/actions';
import { InvoicePaymentUpsertAction } from 'components/InvoicePayment/actions';
import useQDialogs from 'components/QDialogs/useQDialogs';
import { showTxnDetailsDialog } from 'components/Transactions/DetailsDialog/actions';
import { useOpenInvoice } from 'containers/Invoices/invoiceHooks';
import useTxnActions from 'data/transactionActions/useTxnActions';
import { useAppDispatch, useAppSelector, useAppStore } from 'hooks/redux';

import { billerAccountsSelectors } from 'app-components/flux/biller-accounts';
import { billerLoginsUtils } from 'app-components/flux/biller-logins';
import { billerLoginsSelectors } from 'app-components/flux/biller-logins';
import { scheduledTransactionsSelectors } from 'app-components/flux/scheduled-transactions';
import { tracker } from 'app-components/utils/core';
import { getLength } from 'app-components/utils/core/immutableHelpers';

import { billPresentmentSelectors } from 'app-components/flux/bill-presentment';

import { DIALOG_TYPE as DIALOG_CONFIRMATION, mkConfirmationDialogProps } from '../Dialogs/ConfirmationDialog';
import type { InvoicePaymentFormProps } from '../InvoicePayment/types';
import { QButtonVariantEnum } from '../QButton';
import type { TxnOptionProps } from './types';
import { useInvoiceOptionsRaw } from './useInvoiceOptionsRaw';

const useStyles = makeStyles()((theme) => ({
  hiddenIcon: {
    marginRight: 16,
    color: theme.palette.text.secondary,
    minWidth: '24px !important',
    opacity: 0,
  },
  highlightedIcon: {
    marginRight: 16,
    minWidth: '24px !important',
    color: theme.palette.primary.main,
  },
  imgClass: {
    marginLeft: 12,
    height: 24,
    width: 24,
    color: theme.palette.text.primary,
    '&.blue': {
      color: theme.palette.primary.main,
    },
  },
}));

type HookFunction<Props, ReturnType> = (props: Props) => ReturnType;

const createConditionalOptionsHook =
  <Props, ReturnType>(hook: HookFunction<Props, ReturnType>) =>
  (props: Props, isValid?: (props: Props) => boolean): ReturnType | [] => {
    if (!isValid || !isValid(props)) return []; // Validate using the provided function
    return hook(props); // Execute the hook if valid
  };

export const useEditOptions = (props: TxnOptionProps) => {
  const { closeMenu, txnActionProps, txnActionCallback, context } = props;
  const { form, txnToEdit: txn, attributes } = txnActionProps;
  const { isReminder, isRefundIncompleteTxn } = attributes;
  const dispatch = useAppDispatch();

  const handleOpenInvoice = useOpenInvoice('transactions-menu');

  const handleOpenTxnDetails = (txnActionCallback: (() => void) | undefined) => {
    if (form === 'INVESTMENT_TRANSACTION_DETAILS') {
      dispatch(openInvestmentTransactionEditDialogAction(txn.toJS()));
    } else if (isRefundIncompleteTxn) {
      dispatch(
        rootUiActions.createDialog(
          rootUiTypes.mkRootUiData({
            id: 'refund-card',
            type: DIALOG_TYPE_REFUND_EDIT,
            allowNesting: true,
            props: Map({ transaction: txn }),
          }),
        ),
      );
    } else if (isReminder) {
      dispatch(editReminderDialog(txn, context));
    } else {
      dispatch(showTxnDetailsDialog({ txn, txnActionCallback, wrapperId: context }));
    }
    closeMenu();
  };

  const options = [];
  switch (form) {
    case 'INVOICE': {
      let title = 'Invoice';
      const label = (() => {
        switch (txn.subtype) {
          case TransactionSubType.Estimate:
            title = 'Estimate';
            return 'View Estimate';
          case TransactionSubType.InvoiceCreditMemo:
            title = 'Credit Memo';
            return 'View Credit Memo';
          case TransactionSubType.SalesReceipt:
            title = 'Sales Receipt';
            return 'View Sales Receipt';
          case TransactionSubType.SalesRefund:
            title = 'Sales Refund';
            return 'View Sales Refund';
          case TransactionSubType.Invoice:
          default:
            return 'View Invoice';
        }
      })();
      options.push({
        id: 'txn-menu-edit',
        label,
        onSelect: () => {
          handleOpenInvoice({ invoice: txn, initialView: txn.sentOn ? 'Preview' : 'Edit', titleProp: title });
          closeMenu();
        },
        startIcon: <SubdirectoryArrowRightRoundedIcon />,
        divider: true,
      });
      break;
    }
    case 'INVESTMENT_TRANSACTION_DETAILS':
    case 'TRANSACTION_DETAILS': {
      let label = 'Edit transaction';
      if (isReminder) {
        label = 'Edit Reminder';
      } else if (isRefundIncompleteTxn) {
        label = 'Edit Refund';
      }
      options.push({
        id: 'txn-menu-edit',
        label,
        onSelect: () => handleOpenTxnDetails(txnActionCallback),
        startIcon: <EditIcon />,
        divider: true,
      });
      break;
    }
    case 'NONE':
    default:
      break;
  }

  return options;
};

export const usePaymentOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionProps } = props;
  const { attributes, txnToEdit: txn, txnOnOtherSide, matchingTransferIfTransfer, businessEnabled } = txnActionProps;
  const { isFutureDated } = attributes;

  const incomeCOAs = useAppSelector(categoriesSelectors.getIncomeCOAIds);
  const account = useAppSelector((state) => accountsSelectors.getAccountById(state, txn.accountId));
  const canBePaymentToAccountsReceivable = transactionsUtils.canBePaymentToAccountsReceivable(
    txn,
    account,
    incomeCOAs,
    matchingTransferIfTransfer,
  );
  const canBeRefundFromAccountsReceivable = transactionsUtils.canBeRefundFromAccountsReceivable(
    txn,
    account,
    matchingTransferIfTransfer,
  );

  const dispatch = useAppDispatch();

  const startInvoicePayment = (action: InvoicePaymentFormProps['action']) => () => {
    closeMenu();
    dispatch(
      InvoicePaymentUpsertAction({
        action,
        context,
        initIdType: 'PAYMENT',
        initId: txn.id,
        initScreen: 'MAIN',
      }),
    );
  };

  const options: any[] = [];
  if (attributes.isFutureDated || attributes.isReminder || !businessEnabled) {
    return options;
  }
  if (!attributes.isOtherSideInAccountsReceivable) {
    if (canBePaymentToAccountsReceivable) {
      options.push({
        id: 'txn-menu-record-as-payment',
        key: 'txn-menu-record-as-payment',
        startIcon: <PaymentsOutlinedIcon />,
        label: 'Apply Payment',
        onSelect: startInvoicePayment('RECORD_PAYMENT'),
        divider: true,
      });
    }
    if (canBeRefundFromAccountsReceivable && !isFutureDated) {
      options.push({
        id: 'txn-menu-record-as-refund',
        key: 'txn-menu-record-as-refund',
        startIcon: <PaymentsOutlinedIcon />,
        label: 'Apply Refund',
        onSelect: startInvoicePayment('RECORD_REFUND'),
        divider: true,
      });
    }
  }
  // if transfer txn
  if (txnOnOtherSide) {
    if (
      (attributes.isInAccountsReceivable && !transactionsUtils.isDebitTxn(txn)) ||
      (attributes.isOtherSideInAccountsReceivable &&
        txnOnOtherSide?.type !== 'INVOICE' &&
        transactionsUtils.isDebitTxn(txn))
    ) {
      options.push({
        id: 'txn-menu-edit-credit-allocations',
        key: 'txn-menu-edit-credit-allocations',
        startIcon: <PaymentsOutlinedIcon />,
        label: attributes.hasAllocations ? 'Edit Payment' : 'Apply Payment',
        onSelect: startInvoicePayment('EDIT_ALLOCATIONS'),
      });
    }
    if (
      (attributes.isInAccountsReceivable && transactionsUtils.isDebitTxn(txn)) ||
      (attributes.isOtherSideInAccountsReceivable &&
        txnOnOtherSide?.type !== 'INVOICE' &&
        !transactionsUtils.isDebitTxn(txn))
    ) {
      options.push({
        id: 'txn-menu-edit-debit-allocations',
        key: 'txn-menu-edit-debit-allocations',
        startIcon: <PaymentsOutlinedIcon />,
        label: attributes.hasAllocations ? 'Edit Refund' : 'Apply Refund',
        onSelect: startInvoicePayment('EDIT_ALLOCATIONS'),
      });
    }
  }

  return options;
};

export const useGoalOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionCallback, txnActionProps } = props;
  const { txnToEdit: txn } = txnActionProps;

  const goalOptionsFF = useAppSelector((state) => featureFlagsSelectors.getFeatureFlag(state, 'goalWithdrawTxns'));

  const { saveTransactions, deleteTransactions } = useTxnActions({
    wrapperId: context,
    callback: txnActionCallback,
  });

  const goals = useAppSelector(goalsSelectors.getResourcesById);
  const matchingGoal = goals.find((goal: Goal) => txn?.tags?.map((tag: any) => tag?.id).includes(goal.tagId));

  const matchingTxn = useAppSelector((state) =>
    transactionsSelectors.getGoalTxnsForIdAmountAndDate(state, {
      acctId: matchingGoal?.accountId,
      amount: txn.amount,
      date: txn.postedOn,
    }),
  ).first();

  const goalTagIds = goals.map((goal: Goal) => goal.tagId).toList();
  const isGoalTxn = txn?.tags?.some((tag: any) => goalTagIds.includes(tag?.id)) && txn?.coa?.type !== 'ACCOUNT';

  const unlinkFromGoal = () => {
    deleteTransactions([matchingTxn]);
    saveTransactions([
      txn.set(
        'tags',
        (txn?.tags as List<Tag> | undefined)?.filter((tag: Tag) => !goalTagIds.includes(tag.id)),
      ),
    ]);
    setTimeout(() => {
      closeMenu();
    }, 1000);
  };

  const options: any[] = [];

  if (isGoalTxn && goalOptionsFF) {
    options.push({
      label: 'Goals',
      isSubheader: true,
    });
    options.push({
      label: 'Unlink from goal',
      onSelect: unlinkFromGoal,
      startIcon: <LinkOffRoundedIcon />,
    });
  }

  return options;
};

export const useExcludeOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionCallback, txnActionProps, hideExcludeReports, hideExcludeSpendingPlan } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;

  const { classes, cx } = useStyles();
  const theme = useTheme();

  const { saveTransactions } = useTxnActions({
    wrapperId: context,
    callback: txnActionCallback,
  });

  const account = useAppSelector((state) => accountsSelectors.getAccountById(state, txn.accountId));

  const isInvestmentTxn = account?.type === 'INVESTMENT';
  const isInvoiceTxnType = txn.type === 'INVOICE';

  const disableExcludeReports =
    txn?.coa === null ? false : chartOfAccountsUtils.disableExcludeReports(txn?.coa, txn?.split);

  const options: any[] = [];
  if (
    attributes.isInAccountsReceivable ||
    attributes.isOtherSideInAccountsReceivable ||
    isInvestmentTxn ||
    isInvoiceTxnType
  ) {
    return options;
  }

  const doExcludeReports = () => {
    saveTransactions([txn.set('isExcludedFromReports', !txn.isExcludedFromReports)]);
    // delay closing the menu to allow the user to see the checkmark toggling on or off
    setTimeout(() => {
      closeMenu();
    }, 1000);
  };
  const doExcludeF2S = () => {
    saveTransactions([txn.set('isExcludedFromF2S', !txn.isExcludedFromF2S)]);
    // delay closing the menu to allow the user to see the checkmark toggling on or off
    setTimeout(() => {
      closeMenu();
    }, 1000);
  };

  options.push({
    label: 'Exclude from:',
    isSubheader: true,
  });
  if (!hideExcludeSpendingPlan) {
    options.push({
      label: 'Spending Plan',
      onSelect: doExcludeF2S,
      labelColor: txn.isExcludedFromF2S ? theme.palette.primary.main : theme.palette.text.primary,
      startIcon: (
        <ListItemIcon classes={{ root: txn.isExcludedFromF2S ? classes.highlightedIcon : classes.hiddenIcon }}>
          <CheckIcon />
        </ListItemIcon>
      ),
      endIcon: <ExcludeFromSpendingPlan className={cx(classes.imgClass, txn.isExcludedFromF2S ? 'blue' : '')} />,
      divider: hideExcludeReports,
    });
  }
  if (!hideExcludeReports) {
    options.push({
      label: 'Reports',
      onSelect: doExcludeReports,
      labelColor: txn.isExcludedFromReports ? theme.palette.primary.main : theme.palette.text.primary,
      startIcon: (
        <ListItemIcon classes={{ root: txn.isExcludedFromReports ? classes.highlightedIcon : classes.hiddenIcon }}>
          <CheckIcon />
        </ListItemIcon>
      ),
      endIcon: <ExcludeFromReports className={cx(classes.imgClass, txn.isExcludedFromReports ? 'blue' : '')} />,
      disabled: disableExcludeReports,
      divider: true,
    });
  }

  return options;
};

export const useRecurringOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionCallback, txnActionProps } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;
  const store = useAppStore();

  const dispatch = useAppDispatch();

  const { performTransactionAction, saveTransactions } = useTxnActions({
    callback: txnActionCallback,
    wrapperId: context,
  });

  const isIncome = categoriesSelectors.isIncomeCat(store.getState(), txn?.coa?.id ?? '');
  const account = accountsSelectors.getAccountById(store.getState(), txn.accountId);

  const isInvestmentTxn = account?.type === 'INVESTMENT';
  const isInvoiceTxnType = txn.type === 'INVOICE';
  const isPaymentTxnType = txn.type === 'PAYMENT';

  const isPersonalRefund = TransactionSubType.Refund;

  // TODO: Should other side of payments to invoices be included in recurring options (can manually create one so why not?)

  const options: any[] = [];
  if (
    attributes.isInAccountsReceivable ||
    attributes.isOtherSideInAccountsReceivable ||
    isInvestmentTxn ||
    isInvoiceTxnType ||
    isPaymentTxnType ||
    attributes.isReminder
  ) {
    return options;
  }

  const toggleBill = () => {
    saveTransactions([txn.set('isBill', !txn.isBill)]);
    closeMenu();
  };
  const createRecurring = () => {
    dispatch(showTxnDetailsDialog({ txn, recurring: 'create', wrapperId: context }));
    closeMenu();
  };
  const linkRecurring = () => {
    if (txn.stModelId) {
      performTransactionAction({ action: 'unlink-to-scheduled-txn', id: txn.id });
    } else {
      dispatch(showTxnDetailsDialog({ txn, recurring: 'link', wrapperId: context }));
    }
    closeMenu();
  };

  const shouldShowOneTimeBill = !isIncome && !isPersonalRefund;
  const shouldShowNewRecurring = !isPersonalRefund;

  options.push({
    label: 'Recurring',
    isSubheader: true,
  });
  if (!txn?.stModelId) {
    if (shouldShowOneTimeBill) {
      options.push({
        label: 'Mark as one-time bill',
        onSelect: toggleBill,
        startIcon: txn.isBill ? <LinkOffRoundedIcon /> : <RepeatOneIcon />,
      });
    }
    if (shouldShowNewRecurring) {
      options.push({
        label: 'Create new recurring',
        onSelect: createRecurring,
        startIcon: <RepeatIcon />,
      });
    }
  }
  options.push({
    label: txn.stModelId ? 'Un-link recurring' : 'Link to existing recurring',
    onSelect: linkRecurring,
    startIcon: <LinkRoundedIcon />,
  });

  return options;
};

export const useDeleteOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionProps } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;

  const dispatch = useAppDispatch();

  const { deleteTransactions, performTransactionAction } = useTxnActions({ wrapperId: context, robotMode: true });
  const prevReminders = useAppSelector(
    (state) =>
      getLength(
        transactionsSelectors.getPreviousUnlinkedReminders(state, { stModelId: txn.stModelId, postedOn: txn.postedOn }),
      ) > 0,
  );
  const categoryName = chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, true);

  const options: any[] = [];

  if (attributes.txnToEditWasFlipped || (attributes.isInAccountsReceivable && txn.type !== 'INVOICE')) {
    return options;
  }

  let label;
  let content;
  let confirmLabel;
  switch (txn.subtype) {
    case TransactionSubType.Estimate:
      label = 'Delete Estimate';
      content = 'Are you sure want to delete this estimate?';
      confirmLabel = label;
      break;
    case TransactionSubType.InvoiceCreditMemo:
      label = 'Delete Credit Memo';
      content =
        'Are you sure want to delete this credit memo? Any credits from this credit memo that has been allocated ';
      content += 'to debit transactions will be unallocated.';
      confirmLabel = label;
      break;
    case TransactionSubType.SalesReceipt:
      label = 'Delete Sales Receipt';
      content = 'Are you sure want to delete this sales receipt?';
      confirmLabel = label;
      break;
    case TransactionSubType.SalesRefund:
      label = 'Delete Sales Refund';
      content = 'Are you sure want to delete this sales refund?';
      confirmLabel = label;
      break;
    case TransactionSubType.Refund:
      label = 'Delete Refund';
      content = 'Are you sure want to delete this refund?';
      confirmLabel = label;
      break;
    case TransactionSubType.Invoice:
      label = txn.sentOn ? 'Delete Invoice' : 'Delete Draft Invoice';
      content = txn.sentOn
        ? 'After deletion, associated payments will be removed from this invoice and still remain in your account. \n\nInvoiced items will be associated with the client as Ready to Bill.'
        : 'After deletion, all project items and expenses will remain linked to the client.';
      confirmLabel = 'Delete Invoice';
      break;
    default:
      break;
  }

  if (attributes.isReminder) {
    label = 'Dismiss reminder';
    content = `Are you sure you want to dismiss this reminder? \n\n${prevReminders ? 'This will also dismiss all earlier reminders in the series.' : ''} Future reminders won’t be affected. `;
    confirmLabel = label;
  }

  if (label && content && confirmLabel) {
    options.push({
      id: 'delete-invoice',
      label,
      labelColor: 'error',
      startIcon: <DeleteIcon sx={{ color: 'error.main' }} />,
      divider: true,
      onSelect: () => {
        closeMenu();
        dispatch(
          rootUiActions.createDialog(
            rootUiTypes.mkRootUiData({
              id: 'delete-txn-confirmation',
              type: DIALOG_CONFIRMATION,
              allowNesting: true,
              props: Map(
                mkConfirmationDialogProps({
                  title: label,
                  content,
                  confirmLabel,
                  confirmButtonVariant: QButtonVariantEnum.DELETE,
                  denyLabel: 'Cancel',
                  confirmAction: () => {
                    if (attributes.isReminder) {
                      performTransactionAction({
                        action: 'skip',
                        id: txn.id,
                      });
                      tracker.track(tracker.events.deleteReminder, {
                        source: window.location.pathname.split('/').filter(Boolean)[0] ?? 'dashboard',
                        category: categoryName,
                      });
                    } else {
                      deleteTransactions([txn]);
                    }
                  },
                  denyAction: () => dispatch(rootUiActions.removeDialog()),
                }),
              ),
            }),
          ),
        );
      },
    });
  }

  return options;
};

export const useLinkToTransactionOptions = (props: TxnOptionProps) => {
  const { closeMenu, context, txnActionProps, txnActionCallback } = props;
  const { attributes, txnToEdit: txn } = txnActionProps;
  const { isPersonalRefund, isRefundIncompleteTxn, isReminder } = attributes;

  const { performTransactionAction } = useTxnActions({
    callback: txnActionCallback,
    wrapperId: context,
  });

  const dispatch = useAppDispatch();
  const hasStModelId = !!txn.stModelId;
  const linkTxn =
    hasStModelId ||
    (txn.subtype === transactionsTypes.TransactionSubType.A2aTransfer && txn?.matchState === 'NOT_MATCHED');
  const categoryName = chartOfAccountsSelectors.getCoaStringSelector(undefined, txn.coa, true);

  const startMatch = () => {
    const source =
      window.location.pathname === '/'
        ? 'dashboard'
        : window.location.pathname.replaceAll?.(/[/-]|(\d+)/g, (match) =>
            match === '/' ? '' : match === '-' ? ' ' : 'xxx',
          );
    dispatch(showMatchTxnDialog({ txn, wrapperId: context }));
    tracker.track(tracker.events.linkReminderToTxn, {
      source: source,
      category: categoryName,
    });
    if (isRefundIncompleteTxn) tracker.track(tracker.events.refundManualMatch, { location: 'upcoming' });

    closeMenu();
  };

  const unMatch = () => {
    if (txn) {
      performTransactionAction({
        action: isPersonalRefund ? 'unmatch' : 'unlink-to-scheduled-txn',
        id: txn.id,
      });
    }
    closeMenu();
  };

  const options: any[] = [];

  if (linkTxn) {
    options.push({
      id: 'menu-link',
      label: isReminder ? 'Link to transaction' : 'Unlink transaction',
      onSelect: () => (isReminder ? startMatch() : unMatch()),
      startIcon: <CompareArrowsIcon />,
      subtext: isReminder ? 'Match with your recent activity' : '',
      divider: true,
    });
  }

  return options;
};

export const useBillPresentmentOptions = (props: TxnOptionProps) => {
  const { closeMenu, txnActionProps } = props;
  const { txnToEdit: txn } = txnActionProps;

  const { dialogAlert } = useQDialogs();
  const model = useAppSelector((state) =>
    scheduledTransactionsSelectors.getScheduledTransactionById(state, txn?.stModelId || ''),
  );
  const connectOption = useAppSelector((state) => billPresentmentSelectors.getShowConnectOption(state, model));

  const billGoAccount = useAppSelector((state) =>
    billerAccountsSelectors.getBillerAccountForId(state, model?.billPresentmentAccountId),
  );
  const billGoLogin = useAppSelector((state) =>
    billerLoginsSelectors.getBillerLoginForId(state, billGoAccount?.billerLoginID),
  );
  const reconnect =
    model &&
    (model.billerEnrollmentStatus === 'FAILED' ||
      model.billerEnrollmentStatus === 'DISCONNECTED' ||
      billerLoginsUtils.inError(billGoLogin));

  const options: any[] = [];
  const dispatch = useAppDispatch();

  if (!(connectOption || reconnect)) {
    return options;
  }
  const startConnect = () => {
    dispatch(doBillPresentment(model, location));
    closeMenu();
    tracker.track(reconnect ? tracker.events.fixBillerStart : tracker.events.addBillerStart, { initiator: location });
  };

  const unlink = () => {
    dialogAlert(
      'Disconnect biller',
      `Your ${billGoAccount?.nickName || billGoAccount?.name}
        bill reminders will no longer be updated with the latest bill information.`,
      // @ts-expect-error FIXME TS7006
      (btnObj) => {
        if (btnObj.btnPressed === 'Disconnect') {
          HandleDisconnectOption(dispatch, model, billGoLogin, location, true);
        }
      },
      ['Cancel', 'Disconnect'],
    );
  };

  if (connectOption || reconnect) {
    options.push({
      label: reconnect ? 'Reconnect biller' : 'Connect biller',
      onSelect: startConnect,
      startIcon: <LinkIcon />,
      id: 'menu-connect',
      divider: true,
    });
  }
  if (model?.billPresentmentAccountId) {
    options.push({
      startIcon: <UnlinkIcon />,
      label: 'Disconnect biller',
      id: 'menu-unlink',
      onSelect: unlink,
    });
  }
  return options;
};

export const useViewSeriesOptions = (props: TxnOptionProps) => {
  const { closeMenu, txnActionProps } = props;
  const { txnToEdit: txn } = txnActionProps;

  const dispatch = useAppDispatch();

  const options: any[] = [];

  if (!txn.stModelId) {
    return options;
  }

  const openView = () => {
    dispatch(showViewSeriesDialog(txn));
    closeMenu();
  };

  options.push({
    label: 'View Series',
    id: 'menu-view',
    onSelect: openView,
    startIcon: <OpenInBrowserIcon />,
    divider: true,
  });

  return options;
};

export const useInvoiceOptions = createConditionalOptionsHook(useInvoiceOptionsRaw);
